import type {ConnectorId} from '@cohort/shared/schema/apps';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {isDark} from '@cohort/shared-frontend/utils/isDark';
import {Apps} from '@cohort/wallet/apps';
import type {AuthComponentProps} from '@cohort/wallet/apps/AuthComponent';
import OAuthConnectButton from '@cohort/wallet/apps/OAuthConnectButton';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {appsKeys, useAppConnection} from '@cohort/wallet/hooks/api/Apps';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {ConnectionsApi} from '@cohort/wallet/lib/Endpoints';
import type {GetConnectionResponseWDto} from '@cohort/wallet-schemas/connections';
import {CheckCircle} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import React, {Fragment} from 'react';
import {Trans, useTranslation} from 'react-i18next';

const Username: React.FC<{connection: NonNullable<GetConnectionResponseWDto['connection']>}> = ({
  connection,
}) => {
  const {hasDarkBg} = useThemeContext();
  const {t} = useTranslation('apps', {
    keyPrefix: 'oauthComponent',
  });

  return (
    <div className={classNames('text-sm', hasDarkBg ? 'text-gray/30' : 'text-gray/50')}>
      {connection.profile.username ? (
        <Trans
          i18nKey="oauthComponent.loggedInAs"
          ns="apps"
          values={{username: connection.profile.username}}
          components={{
            username: <span className="font-bold" />,
          }}
        />
      ) : (
        <div>{t('loggedIn')}</div>
      )}
    </div>
  );
};

export const OAuthComponent: React.FC<AuthComponentProps> = ({appSpec}) => {
  const {t} = useTranslation('apps', {
    keyPrefix: 'oauthComponent',
  });
  const {accentColor, hasDarkBg} = useThemeContext();
  const {isMobile} = useScreenSizeContext();
  const app = Apps.find(app => app.spec.id === appSpec.id);
  const deleteConnection = useApi(ConnectionsApi.deleteConnection);
  const queryClient = useQueryClient();
  const connectorId = appSpec.userConnector.id as ConnectorId;
  const {data: connection, isFetched} = useAppConnection(connectorId);

  const {mutate: revokeOauth, isLoading: isRevoking} = useCohortMutation({
    mutationFn: async () => await deleteConnection(connection?.id || ''),
    onSuccess: () => {
      queryClient.invalidateQueries(appsKeys.appConnection(connectorId));
    },
  });

  return (
    <div
      className={classNames(
        'my-2 flex flex-col gap-2 rounded-lg border p-4',
        hasDarkBg ? 'border-white/20' : 'border-black/20'
      )}
    >
      <div className="flex flex-col gap-4 md:flex-row md:justify-between">
        <div className="flex items-center gap-4 font-bold">
          {!!app?.logo && React.cloneElement(app.logo, {height: 32, width: 32})}

          <h2>{appSpec.name}</h2>
        </div>

        {connection ? (
          <div className="flex flex-row items-center gap-1 text-sm">
            <CheckCircle className="h-6 w-6 text-green-700" aria-hidden="true" />
            <p className="text-green-700">{t('connectedLabel')}</p>
            {isMobile && (
              <Fragment>
                &nbsp;-&nbsp;
                <Username connection={connection} />
              </Fragment>
            )}
          </div>
        ) : (
          <OAuthConnectButton
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            app={app!}
            text={t('connectApp', {appName: appSpec.name})}
            style={{backgroundColor: accentColor}}
            className="h-8 w-full"
            connectorId={connectorId}
            xpsRedirectUrl={window.location.href}
            tracking={{namespace: 'apps.connect', metadata: {appId: appSpec.id}}}
          />
        )}
      </div>
      {isFetched && connection && (
        <div className="flex flex-col items-center gap-2 md:flex-row md:justify-between">
          {!isMobile && <Username connection={connection} />}
          {/* Disconnect button */}
          <PrimaryButton
            className={classNames('h-8', isMobile && 'mt-2 w-full')}
            isLoading={isRevoking}
            style={{backgroundColor: accentColor, color: isDark(accentColor) ? 'white' : 'black'}}
            text={t('disconnectApp', {appName: appSpec.name})}
            onClick={() => revokeOauth()}
            tracking={{namespace: 'apps.disconnect', metadata: {appId: appSpec.id}}}
          />
        </div>
      )}
    </div>
  );
};
